import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap as useGoogleMap } from '@vis.gl/react-google-maps';
import { useMapStaleGuard } from '../hooks/useMapStaleGuard';
import { MapContainer, TileLayer, Marker as LeafletMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2 } from 'lucide-react';
import { deviceDisplayName } from '../utils/deviceName';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;

const STATUS_COLORS = {
  moving: '#2563eb', online: '#16a34a', idle: '#ca8a04', stopped: '#dc2626', offline: '#6b7280',
};

const ArrowMarker = ({ status, heading = 0 }) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.offline;
  return (
    <div style={{ width: 0, height: 0, overflow: 'visible' }}>
      <div style={{
        width: '52px', height: '52px',
        transform: `translate(-50%, -50%) rotate(${heading}deg)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
      }}>
        <svg width="52" height="52" viewBox="0 0 24 24" fill={color} stroke="white" strokeWidth="1.5">
          <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
        </svg>
      </div>
    </div>
  );
};

const GooglePanner = ({ lat, lng }) => {
  const map = useGoogleMap();
  const prevRef = useRef(null);
  useEffect(() => {
    if (map && lat && lng) {
      const key = `${lat},${lng}`;
      if (key !== prevRef.current) { map.panTo({ lat, lng }); prevRef.current = key; }
    }
  }, [map, lat, lng]);
  return null;
};

const LeafletPanner = ({ lat, lng }) => {
  const map = useMap();
  const prevRef = useRef(null);
  useEffect(() => {
    if (lat && lng) {
      const key = `${lat},${lng}`;
      if (key !== prevRef.current) { map.panTo([lat, lng]); prevRef.current = key; }
    }
  }, [map, lat, lng]);
  return null;
};

const createLeafletArrow = (status, heading) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.offline;
  return L.divIcon({
    className: 'follow-marker',
    html: `<div style="width:52px;height:52px;transform:rotate(${heading}deg);display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">
      <svg width="52" height="52" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5"><path d="M12 2l-5 18.5L12 17l5 3.5z"/></svg></div>`,
    iconSize: [52, 52], iconAnchor: [26, 26],
  });
};

export default function FollowDevice() {
  const { api } = useAuth();
  const { hasGoogleMaps, hasOSMMaps } = useFeatureFlags();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const gmapEpoch = useMapStaleGuard();

  const params = new URLSearchParams(window.location.search);
  const deviceId = params.get('id');
  const mapStyle = params.get('map') || 'roadmap'; // roadmap, satellite, terrain, hybrid, osm
  const isDark = params.get('dark') === '1';
  const showPOI = params.get('poi') === '1';
  const useOSM = mapStyle === 'osm' || (!hasGoogleMaps && hasOSMMaps);
  const useGoogle = !useOSM && hasGoogleMaps && GOOGLE_MAPS_API_KEY;

  const fetchDevice = useCallback(async () => {
    if (!deviceId) return;
    try {
      const res = await api.get('/gps/live');
      const d = res.data.find(dev => dev.id === deviceId);
      if (d) setDevice(d);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [api, deviceId]);

  useEffect(() => { fetchDevice(); }, [fetchDevice]);
  useEffect(() => {
    if (!deviceId) return;
    const interval = setInterval(fetchDevice, 5000);
    return () => clearInterval(interval);
  }, [fetchDevice, deviceId]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" /></div>;
  }
  if (!device) {
    return <div className="flex items-center justify-center h-screen bg-background"><p className="text-muted-foreground">Device not found</p></div>;
  }

  const lat = device.last_latitude;
  const lng = device.last_longitude;
  const name = deviceDisplayName(device);
  const speed = device.last_speed != null ? `${Math.round(device.last_speed)} kph` : '';
  const statusLabel = device.status?.charAt(0).toUpperCase() + device.status?.slice(1);

  return (
    <div className="h-screen w-screen relative">
      {/* Info bar */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-xl border px-4 py-2 flex items-center gap-4" data-testid="follow-info-bar">
        <div>
          <p className="font-semibold text-sm">{name}</p>
          <p className="text-xs text-muted-foreground">{statusLabel} {speed && `· ${speed}`}</p>
        </div>
        <div className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS[device.status] || STATUS_COLORS.offline }} />
      </div>

      {lat && lng ? (
        useGoogle ? (
          <APIProvider key={`gmap-${gmapEpoch}`} apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              defaultCenter={{ lat, lng }}
              defaultZoom={17}
              mapId={showPOI ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
              colorScheme={isDark ? 'DARK' : 'LIGHT'}
              mapTypeId={mapStyle !== 'osm' ? mapStyle : 'roadmap'}
              style={{ width: '100%', height: '100%' }}
              disableDefaultUI={true}
              zoomControl={true}
            >
              <GooglePanner lat={lat} lng={lng} />
              <AdvancedMarker position={{ lat, lng }}>
                <ArrowMarker status={device.status} heading={device.last_heading || 0} />
              </AdvancedMarker>
            </GoogleMap>
          </APIProvider>
        ) : (
          <MapContainer center={[lat, lng]} zoom={17} className="h-full w-full" scrollWheelZoom zoomControl>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LeafletPanner lat={lat} lng={lng} />
            <LeafletMarker position={[lat, lng]} icon={createLeafletArrow(device.status, device.last_heading || 0)} />
          </MapContainer>
        )
      ) : (
        <div className="flex items-center justify-center h-full bg-muted"><p className="text-muted-foreground">No GPS position available</p></div>
      )}
    </div>
  );
}
